import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog, DialogTitle, IconButton, DialogContent,
  Button, Table, TableBody, TableCell, TableHead, TableRow, DialogActions,
  TableContainer, Grid, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { UserContext } from '../../../components/shared/useUser';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import { client, OrderDetails } from '../../../app/services/api/orderManagementClient';
import { useTranslation } from 'react-i18next';

interface DuplicateOrderModalProps {
  currentOrderId: number;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DuplicateOrderModal: React.FC<DuplicateOrderModalProps> = ({ currentOrderId, open, onClose, onConfirm }) => {

  const { selectedStore } = useContext(UserContext);
  const { t } = useTranslation('common');

  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | undefined>(undefined);

  useEffect(() => {
    if (open && currentOrderId) {
      fetchOrderDetails();
    }
  }, [open, currentOrderId, selectedStore]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      const response = await client.getOrder(selectedStore?.storeNumber, currentOrderId);
      if (response.success) {
        setOrderDetails(response.data);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const handleClose = () => {
    setOrderDetails(undefined);
    onClose();
  };

  return (
    <Dialog open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth>
      <DialogTitle>
        Duplicate Order
        <IconButton onClick={handleClose}
          aria-label={t('close')}
          style={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <LoadingWheel />
        ) : (
          <>
            <Alert severity="info"
              sx={{ marginTop: 2, marginBottom: 2 }}>
              This will create a new order based on the items from Order #{currentOrderId}. Do you want to continue?
            </Alert>

            {orderDetails && (
              <>
                <div style={{ fontWeight: 'bold', marginTop: 16, marginBottom: 8 }}>
                  Order Details
                </div>
                <Grid container
                  spacing={2}
                  sx={{ marginBottom: 2 }}>
                  <Grid item
                    xs={6}>
                    <strong>Order Type:</strong> {orderDetails.orderType}
                  </Grid>
                  <Grid item
                    xs={6}>
                    <strong>Status:</strong> {orderDetails.status}
                  </Grid>
                  <Grid item
                    xs={6}>
                    <strong>Delivery Date:</strong> {orderDetails.deliveryDate}
                  </Grid>
                </Grid>

                <div style={{ fontWeight: 'bold', marginTop: 16, marginBottom: 8 }}>
                  Items ({orderDetails.items.length})
                </div>
                <TableContainer component={'div'}
                  sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>SKU</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>UOM</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderDetails.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.sku}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.category}</TableCell>
                          <TableCell>{item.orderableUom}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}
          size="md"
          variant="secondary">
          Cancel
        </Button>
        <Button onClick={handleConfirm}
          size="md"
          variant="primary"
          disabled={loading || !orderDetails}>
          Duplicate Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateOrderModal;
